import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { DollarSign, IdCard, Loader2, ShieldAlert, Truck, UserCheck, UserRound, UserX } from 'lucide-react';
import { api, apiClient, getApiErrorMessage } from '../../api/client';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { unwrap, unwrapArray } from '../../lib/api-envelope';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { DhEmpty, DhPanel, DhSpec } from '../downhole/DownholeBits';
import type { TruckingDriverUpdateResponse, TruckingRosterStatusResponse } from '../../types/trucking-roster';
import {
  CERT_BADGE,
  DriverAccountEditor,
  DriverStatusBadge,
  EditPayRatesModal,
  LicenceBadge,
  PAY_METHOD,
  badgeCls,
  fmtDay,
  isLapsing,
  payRateText,
  skinOf,
  str,
  toDriver,
  type TenantUser,
} from '../../screens/trucking/DriverManagementScreen';

/**
 * One driver, /trucking/drivers/:id (#815: registers + a page per driver, in
 * place of the split pane). GET /trucking/drivers/:id returns the list's
 * shape (the row plus `assigned_truck`); a 404 is "does not exist". Open to
 * trucking and surface rentals tenants alike (api#1041).
 */
export default function DriverPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const timeZone = useTenantTimeZone();
  const [showPayRates, setShowPayRates] = useState(false);
  const [retireOpen, setRetireOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const query = useQuery({
    // Under the list's key, so a save that refreshes the register refreshes this too.
    queryKey: ['trucking-drivers', 'one', id, timeZone],
    queryFn: async () => {
      try {
        const row = unwrap<Record<string, unknown>>((await api.trucking.drivers.get(encodeURIComponent(id))).data);
        return row ? toDriver(row, timeZone) : null;
      } catch (err) {
        // 404 is an answer: no such driver in this company.
        if ((err as { response?: { status?: number } })?.response?.status === 404) return null;
        throw err;
      }
    },
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const failure = queryLoadFailure(query);
  const loading = query.isLoading && failure == null;
  const driver = failure == null ? query.data ?? null : null;

  const usersQuery = useQuery({
    queryKey: ['tenant-users-for-drivers'],
    queryFn: () =>
      api.users.list().then((r) =>
        unwrapArray<Record<string, unknown>>(r.data).map((u): TenantUser => {
          const name = [str(u.first_name), str(u.last_name)].filter(Boolean).join(' ');
          const mail = str(u.email);
          return { id: String(u.id), label: name && mail ? `${name} (${mail})` : name || mail || String(u.id) };
        }),
      ),
    enabled: !!driver,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const usersError = queryLoadFailure(usersQuery) ? getApiErrorMessage(queryFailureError(usersQuery), "Couldn't load the logins.") : null;

  /**
   * PUT /trucking/drivers/:id keeps schema property names only: `payMethod`,
   * `currency`, `userId`, `email`. The pay keys are admin / office-staff
   * only; anyone else gets the API's 403 message here.
   */
  const updateDriver = async (body: Record<string, unknown>, done: string) => {
    try {
      await apiClient.put<TruckingDriverUpdateResponse>(`/trucking/drivers/${encodeURIComponent(id)}`, body);
      toast.success(done);
      await queryClient.invalidateQueries({ queryKey: ['trucking-drivers'] });
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to save the driver'));
    }
  };

  const retire = async () => {
    setBusy(true);
    try {
      await apiClient.delete<TruckingRosterStatusResponse>(`/trucking/drivers/${encodeURIComponent(id)}`);
      toast.success('Driver retired');
      queryClient.invalidateQueries({ queryKey: ['trucking-drivers'] });
      setRetireOpen(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to retire driver'));
    } finally {
      setBusy(false);
    }
  };

  const reactivate = async () => {
    setBusy(true);
    try {
      await apiClient.post<TruckingRosterStatusResponse>(`/trucking/drivers/${encodeURIComponent(id)}/reactivate`);
      toast.success('Driver reactivated');
      queryClient.invalidateQueries({ queryKey: ['trucking-drivers'] });
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to reactivate driver'));
    } finally {
      setBusy(false);
    }
  };

  const toggleCls = (on: boolean) =>
    `rounded-fi-control border px-3 py-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
      on ? 'border-fi-amber bg-fi-amber/10 text-fieldiq-text' : 'border-fieldiq-border text-fieldiq-muted'
    }`;
  const lapsing = driver ? driver.certs.filter(isLapsing).length : 0;

  return (
    <div data-testid="driver-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Driver"
        title={driver ? driver.name : loading ? 'Loading…' : 'Driver'}
        onBack={() => navigate('/trucking/drivers')}
        backLabel="Back to Drivers"
        stats={
          !driver
            ? undefined
            : [
                { icon: <UserRound className="size-4" aria-hidden="true" />, label: 'Status', sub: 'On the roster', tone: 'neutral', value: skinOf(driver.status).label },
                { icon: <Truck className="size-4" aria-hidden="true" />, label: 'Truck', sub: 'Assigned', tone: 'info', value: driver.truck ?? '—' },
                { icon: <IdCard className="size-4" aria-hidden="true" />, label: 'Licence expiry', sub: driver.licence_status === 'expired' ? 'Expired' : (driver.licence_class ?? '—'), tone: driver.licence_status === 'expired' ? 'accent' : 'neutral', value: fmtDay(driver.licence_expiry) },
                { icon: <ShieldAlert className="size-4" aria-hidden="true" />, label: 'Certs due', sub: 'Lapsed or soon', tone: 'accent', value: driver.certs.length === 0 ? '—' : String(lapsing) },
              ]
        }
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading driver" />
            </div>
          ) : !driver ? (
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
              {failure ? (
                <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="this driver" />
              ) : (
                <DhEmpty>This driver does not exist, or was deleted.</DhEmpty>
              )}
            </div>
          ) : (
            <>
              <DhPanel eyebrow="Driver" title="Driver record" testId="driver-detail" actions={<DriverStatusBadge status={driver.status} />}>
                <dl className="grid grid-cols-1 gap-x-6 gap-y-4 p-4 sm:grid-cols-2 md:grid-cols-4">
                  <DhSpec label="Phone" value={driver.phone ?? '—'} />
                  <DhSpec label="Email" value={driver.email ?? '—'} />
                  <DhSpec label="Licence" value={driver.licence_class ?? '—'} />
                  <DhSpec label="Licence no." value={driver.licence_number ?? '—'} />
                  <DhSpec
                    label="Licence expiry"
                    value={
                      <span className="flex flex-wrap items-center gap-2">
                        {fmtDay(driver.licence_expiry)}
                        <LicenceBadge status={driver.licence_status} />
                      </span>
                    }
                  />
                  <DhSpec label="Hired" value={fmtDay(driver.hire_date)} />
                  <DhSpec
                    label="Truck"
                    value={
                      driver.truck_id ? (
                        <Link className="underline underline-offset-2" to={`/trucking/fleet/${encodeURIComponent(driver.truck_id)}`}>
                          {driver.truck ?? 'Open unit'}
                        </Link>
                      ) : (
                        driver.truck ?? '—'
                      )
                    }
                  />
                </dl>
              </DhPanel>

              <div className="grid gap-4 lg:grid-cols-2">
                <DhPanel eyebrow="Account" title="Login" testId="driver-login-panel">
                  <div className="p-4">
                    <DriverAccountEditor
                      key={driver.id + (driver.user_id ?? '') + (driver.email ?? '')}
                      driver={driver}
                      users={usersQuery.data}
                      usersError={usersError}
                      onSave={(body, what) => updateDriver(body, what)}
                    />
                  </div>
                </DhPanel>

                <DhPanel eyebrow="Pay" title="Pay" testId="driver-pay">
                  <div className="space-y-3 p-4">
                    <dl className="grid grid-cols-3 gap-x-4 gap-y-3">
                      <DhSpec label="Method" value={(driver.pay_method && PAY_METHOD[driver.pay_method]) || '—'} />
                      <DhSpec label="Regular rate" value={payRateText(driver)} />
                      <DhSpec label="Currency" value={driver.currency ?? '—'} />
                    </dl>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(PAY_METHOD).map(([value, label]) => (
                        <button key={value} type="button" aria-pressed={driver.pay_method === value} onClick={() => updateDriver({ payMethod: value }, 'Pay method saved')} className={toggleCls(driver.pay_method === value)}>
                          {label}
                        </button>
                      ))}
                      {['CAD', 'USD'].map((cur) => (
                        <button key={cur} type="button" aria-pressed={driver.currency === cur} onClick={() => updateDriver({ currency: cur }, 'Currency saved')} className={toggleCls(driver.currency === cur)}>
                          {cur}
                        </button>
                      ))}
                    </div>
                    <button type="button" onClick={() => setShowPayRates(true)} className="fi-action fi-action--standard fi-action--primary">
                      <DollarSign className="size-4" aria-hidden="true" /> Edit Pay Rates
                    </button>
                  </div>
                </DhPanel>
              </div>

              <DhPanel eyebrow="Safety" title="Certifications" testId="driver-certs">
                {driver.certs.length === 0 ? (
                  <DhEmpty>None on file</DhEmpty>
                ) : (
                  <ul className="divide-y divide-fieldiq-border">
                    {driver.certs.map((c) => (
                      <li key={c.name} data-testid="driver-cert" className="flex items-center justify-between gap-3 px-4 py-3">
                        <div>
                          <p className="text-sm font-semibold text-fieldiq-text">{c.name}</p>
                          <p className="text-[11px] text-fieldiq-muted">Exp {fmtDay(c.expiryDate)}</p>
                        </div>
                        <span className={`${badgeCls} ${CERT_BADGE[c.status].fill}`}>{CERT_BADGE[c.status].label}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </DhPanel>

              <div className="flex flex-wrap gap-2">
                {driver.status === 'retired' ? (
                  <button type="button" disabled={busy} onClick={reactivate} className="fi-action fi-action--standard fi-action--primary disabled:opacity-50">
                    <UserCheck className="size-4" aria-hidden="true" /> Reactivate driver
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => setRetireOpen(true)}
                    className="fi-action fi-action--standard border border-fieldiq-border bg-fieldiq-card text-fieldiq-text hover:bg-fieldiq-dark/50 disabled:opacity-50"
                  >
                    <UserX className="size-4" aria-hidden="true" /> Retire driver
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </section>

      {showPayRates && driver && (
        <EditPayRatesModal
          driverId={driver.id}
          onClose={() => setShowPayRates(false)}
          onSuccess={() => {
            setShowPayRates(false);
            queryClient.invalidateQueries({ queryKey: ['trucking-driver-pay-rates', driver.id] });
          }}
        />
      )}
      <ConfirmModal
        isOpen={retireOpen}
        title={`Retire ${driver?.name ?? 'driver'}?`}
        body={<>
          <p>This driver will be hidden from active driver lists, dispatch, and scheduling. Their historical records (BOLs, timesheets) will remain intact. You can reactivate them later if needed.</p>
          {driver?.truck && <p className="mt-2 text-xs text-fieldiq-text">Assigned truck {driver.truck} will be released.</p>}
        </>}
        confirmLabel="Retire Driver"
        confirmVariant="destructive"
        loading={busy}
        onConfirm={retire}
        onCancel={() => setRetireOpen(false)}
      />
    </div>
  );
}
